import { v4 as uuidv4 } from 'uuid';

import { CLEAR_SITE_DATA } from '../application';
import {
  CREATE_PANEL_VISUALIZATIONS_SUBSCRIPTION,
  DESTROY_PANEL_VISUALIZATIONS_SUBSCRIPTION,
  DESTROY_VISUALIZATIONS_SUBSCRIPTION,
  UPSERT_VISUALIZATION,
  DELETE_VISUALIZATION_SUCCESS,
  RECEIVE_VISUALIZATION,
  RECEIVE_VISUALIZATIONS,
  RECEIVED_VALUES,
  INIT_VISUALIZATIONS,
  RESET_VISUALIZATIONS,
  SET_LOADING,
  VISUALIZATIONS_IMPORT_ERRORS,
  VISUALIZATIONS_IMPORTED_AT,
  RECEIVE_VISUALIZATION_TEMPLATE_TYPES,
} from './actions';

const initialState = {
  visualizations: {},
  originalVisualizations: {},
  subscriptions: {},
  values: {},
  siteLoaded: undefined,
  hasBeenUpdated: false,
  importErrors: [],
  visualizationsImportedAt: 0,
  templateTypes: [],
};

export const reducer = (state = structuredClone(initialState), action) => {
  switch (action.type) {
    case RESET_VISUALIZATIONS: {
      return {
        ...state,
        visualizations: { ...state.originalVisualizations },
        originalVisualizations: { ...state.originalVisualizations },
      };
    }
    case UPSERT_VISUALIZATION: {
      const { visualization } = action;

      const mergedVisualization = {
        ...state.visualizations[visualization.id],
        ...visualization,
      };

      const processedVisualization = mergedVisualization.variables
        ? {
            ...mergedVisualization,
            variables: mergedVisualization.variables.map((variable) => ({
              ...variable,
              aggregate:
                variable.aggregate && variable.aggregate !== 'none' ? variable.aggregate : 'max',
            })),
          }
        : mergedVisualization;

      return {
        ...state,
        visualizations: {
          ...state.visualizations,
          [visualization.id]: processedVisualization,
        },
      };
    }
    case RECEIVE_VISUALIZATION: {
      const { visualization } = action;

      return {
        ...state,
        visualizations: {
          ...state.visualizations,
          [visualization.id]: { ...visualization },
        },
        originalVisualizations: {
          ...state.originalVisualizations,
          [visualization.id]: { ...visualization },
        },
      };
    }
    case RECEIVE_VISUALIZATIONS: {
      const { visualizations, siteId } = action;
      const indexed = Object.fromEntries(visualizations.map((v) => [v.id, v]));

      return {
        ...state,
        visualizations: { ...indexed },
        originalVisualizations: { ...indexed },
        siteLoaded: siteId || state.siteLoaded,
      };
    }
    case DELETE_VISUALIZATION_SUCCESS: {
      const { visualizationId } = action;
      const vizCopy = { ...state.visualizations };
      const origVizCopy = { ...state.originalVisualizations };
      delete vizCopy[visualizationId];
      delete origVizCopy[visualizationId];

      return {
        ...state,
        visualizations: vizCopy,
        originalVisualizations: origVizCopy,
      };
    }
    case RECEIVED_VALUES: {
      const {
        visualizationId,
        latestValues: newLatestValues,
        seriesValues: newSeriesValues,
        panelId,
      } = action;

      const { values } = state;
      const vizValues = values[visualizationId] || [];
      const existingEntry = vizValues.find((item) => item.panelId === panelId);

      const updated = existingEntry
        ? vizValues.map((item) => {
            if (item.panelId === panelId) {
              const { latestValues, seriesValues } = item;
              return {
                panelId,
                latestValues: newLatestValues ?? latestValues,
                seriesValues: newSeriesValues ?? seriesValues,
                loading: false,
                initialLoaded: true,
              };
            }
            return item;
          })
        : [
            ...vizValues,
            {
              panelId,
              latestValues: newLatestValues ?? null,
              seriesValues: newSeriesValues ?? null,
              loading: false,
              initialLoaded: true,
            },
          ];

      return {
        ...state,
        values: {
          ...values,
          [visualizationId]: updated,
        },
      };
    }
    case SET_LOADING: {
      const { visualizationId, loading, panelId } = action;
      const { values } = state;
      const vizValues = values[visualizationId];
      if (vizValues) {
        const updated = vizValues.map((item) => {
          if (item.panelId === panelId) {
            return { ...item, loading };
          }
          return item;
        });

        return {
          ...state,
          values: {
            ...values,
            [visualizationId]: updated,
          },
        };
      }
      return state;
    }
    case CREATE_PANEL_VISUALIZATIONS_SUBSCRIPTION: {
      const { visualizations, panelId } = action;
      const { subscriptions, values } = state;
      const newSubscriptions = { ...subscriptions };
      const newValues = { ...values };

      // Subscribe all visualizations in the panel at once
      visualizations.forEach((visualization) => {
        const visualizationId = visualization.id;
        const newVizSub = [...(newSubscriptions[visualizationId] || [])];
        const vizValues = [...(newValues[visualizationId] || [])];

        const newSub = { panelId, subId: uuidv4() };
        const value = {
          latestValues: null,
          seriesValues: null,
          initialLoaded: false,
          loading: false,
          panelId,
        };

        const valueExists = vizValues.find((v) => v.panelId === panelId);
        const subExists = newVizSub.find((v) => v.panelId === panelId);

        if (!valueExists) {
          vizValues.push(value);
        }
        if (!subExists) {
          newVizSub.push(newSub);
        }

        newSubscriptions[visualizationId] = newVizSub;
        newValues[visualizationId] = vizValues;
      });

      return {
        ...state,
        subscriptions: newSubscriptions,
        values: newValues,
      };
    }
    case DESTROY_PANEL_VISUALIZATIONS_SUBSCRIPTION: {
      const { visualizations, panelId } = action;
      const { subscriptions, values } = state;
      const newSubscriptions = { ...subscriptions };
      const newValues = { ...values };

      // Unsubscribe all visualizations in the panel at once
      visualizations.forEach((visualization) => {
        const visualizationId = visualization.id;
        const newVizSub = [...(newSubscriptions[visualizationId] || [])];
        const vizValues = [...(newValues[visualizationId] || [])];

        const subIndex = newVizSub.findIndex((v) => v.panelId === panelId);
        const valueIndex = vizValues.findIndex((v) => v.panelId === panelId);

        if (subIndex !== -1) {
          newVizSub.splice(subIndex, 1);
          if (newVizSub.length > 0) {
            newSubscriptions[visualizationId] = newVizSub;
          } else {
            delete newSubscriptions[visualizationId];
          }
        }

        if (valueIndex !== -1) {
          vizValues.splice(valueIndex, 1);
          if (vizValues.length > 0) {
            newValues[visualizationId] = vizValues;
          } else {
            delete newValues[visualizationId];
          }
        }
      });

      return {
        ...state,
        subscriptions: newSubscriptions,
        values: newValues,
      };
    }
    case DESTROY_VISUALIZATIONS_SUBSCRIPTION: {
      const { visualizationId, panelId } = action;
      const { subscriptions } = state;
      const { [visualizationId]: viz, ...rest } = subscriptions;

      const vizPanelIdIndex = viz?.findIndex((p) => p.panelId === panelId);

      if (vizPanelIdIndex > -1) {
        viz.splice(vizPanelIdIndex, 1);
        if (viz.length > 0) {
          rest[visualizationId] = viz;
        }
      }

      return {
        ...state,
        subscriptions: {
          ...rest,
        },
      };
    }
    case VISUALIZATIONS_IMPORT_ERRORS: {
      return { ...state, importErrors: action.errors };
    }
    case VISUALIZATIONS_IMPORTED_AT: {
      return {
        ...state,
        visualizationsImportedAt: action.visualizationsImportedAt,
      };
    }
    case RECEIVE_VISUALIZATION_TEMPLATE_TYPES: {
      return { ...state, templateTypes: action.templateTypes };
    }
    case INIT_VISUALIZATIONS:
    case CLEAR_SITE_DATA: {
      return {
        ...structuredClone(initialState),
        // Kept across the reset.
        templateTypes: state.templateTypes,
      };
    }
    default: {
      return state;
    }
  }
};
